import { useState, useEffect, memo } from "react";
import { Link } from "react-router-dom";
import type { AuctionItem } from "@/types/auction";
import { Clock, Flame } from "lucide-react";
import MoneyIcon from "@/components/MoneyIcon";
import PlayerName from "@/components/PlayerName";
import PlayerAvatar from "@/components/PlayerAvatar";
import WatchlistButton from "@/components/WatchlistButton";
import AuctionCardContextMenu from "@/components/auction/AuctionCardContextMenu";
import { generateAuctionOpuID } from "@/lib/opuid";

interface AuctionCardProps {
  auction: AuctionItem;
  showSeller?: boolean;
}

const formatTimeRemaining = (endTime: string): string => {
  const now = Date.now();
  const end = new Date(endTime).getTime();
  const remaining = end - now;
  
  if (remaining <= 0) return "Beendet";
  
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
  
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

const formatPrice = (price: number): string => {
  if (price >= 1_000_000) {
    return `${(price / 1_000_000).toFixed(1)}M`;
  }
  if (price >= 1_000) {
    return `${(price / 1_000).toFixed(1)}K`;
  }
  return price.toLocaleString('de-DE');
};

const getItemIcon = (auction: AuctionItem): string => {
  if (auction.item.icon) {
    return auction.item.icon;
  }
  return `https://img.mc-api.io/${auction.item.material.toLowerCase()}.png`;
};

const AuctionCard = memo(({ auction, showSeller = true }: AuctionCardProps) => {
  const [timeRemaining, setTimeRemaining] = useState(() => formatTimeRemaining(auction.endTime));
  
  // Update time: every second if under 61 minutes, otherwise every 10 seconds
  useEffect(() => {
    const updateTime = () => {
      setTimeRemaining(formatTimeRemaining(auction.endTime));
    };
    
    const getIntervalDuration = () => {
      const remaining = new Date(auction.endTime).getTime() - Date.now();
      // Under 61 minutes = update every second
      return remaining < 61 * 60 * 1000 ? 1000 : 10000;
    };
    
    updateTime();
    
    let intervalId: ReturnType<typeof setInterval>;
    
    const setupInterval = () => {
      const duration = getIntervalDuration();
      intervalId = setInterval(() => {
        updateTime();
        // Check if we need to switch interval frequency
        const newDuration = getIntervalDuration();
        if (newDuration !== duration) {
          clearInterval(intervalId);
          setupInterval();
        }
      }, duration);
    };
    
    setupInterval();
    
    return () => clearInterval(intervalId);
  }, [auction.endTime]);

  const endTime = new Date(auction.endTime).getTime();
  const isEnding = endTime - Date.now() < 1000 * 60 * 60;
  const hasBids = Object.keys(auction.bids).length > 0;
  const uniqueBidders = Object.keys(auction.bids).length;
  const isHot = uniqueBidders > 5;
  const itemName = auction.item.displayName || auction.item.material.replace(/_/g, ' ');
  
  // Generate OpuID for linking
  const auctionId = generateAuctionOpuID(auction.seller, auction.startTime, auction.endTime);
  // Raw auction_key for watchlist storage (matches DB format)
  const rawAuctionKey = `${auction.seller}|${auction.startTime}`;

  return (
    <AuctionCardContextMenu
      auction={auction}
      auctionId={auctionId}
      rawAuctionKey={rawAuctionKey}
    >
      <Link
        to={`/auction/${auctionId}`}
        className={`group block rounded-xl border bg-secondary/30 backdrop-blur-sm overflow-hidden hover:bg-secondary/40 active:scale-[0.98] transition-all duration-200 touch-manipulation relative ${
          isHot 
            ? "border-orange-500/40 shadow-[0_0_12px_-3px_rgba(249,115,22,0.3)] hover:shadow-[0_0_18px_-3px_rgba(249,115,22,0.4)] hover:border-orange-500/60" 
            : "border-border/50 hover:border-border"
        }`}
      >
        {/* Hot indicator icon */}
        {isHot && (
          <div className="absolute top-2 left-2 z-10">
            <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500 drop-shadow-[0_0_4px_rgba(249,115,22,0.6)]" />
          </div>
        )}

        {/* Watchlist Button - bottom right */}
        <div className="absolute bottom-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <WatchlistButton auctionKey={rawAuctionKey} variant="icon" />
        </div>

        <div className="p-3 sm:p-4">
          <div className="flex items-start gap-2.5 sm:gap-3 mb-2.5 sm:mb-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-accent/50 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img 
                src={getItemIcon(auction)} 
                alt={itemName}
                className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                style={{ imageRendering: 'pixelated' }}
                loading="lazy"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://img.mc-api.io/barrier.png';
                }}
              />
            </div>
            <div className="flex-1 min-w-0 pr-6">
              <span className="text-xs sm:text-sm font-semibold text-foreground truncate leading-tight block">
                {auction.item.amount > 1 ? `${auction.item.amount}x ` : ''}{itemName}
              </span>
              <p className="text-[10px] sm:text-xs text-muted-foreground capitalize mt-0.5">
                {auction.category.replace(/_/g, ' ')}
              </p>
            </div>
          </div>
          
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex items-center justify-between">
              {showSeller ? (
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `/player/${auction.seller}`;
                  }}
                  className="flex items-center gap-1.5 text-muted-foreground min-w-0 min-h-6 hover:text-foreground transition-colors cursor-pointer"
                >
                  <PlayerAvatar uuid={auction.seller} size={14} className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <PlayerName uuid={auction.seller} className="text-[10px] sm:text-xs truncate max-w-[80px] sm:max-w-[100px]" />
                </div>
              ) : (
                <div className="flex-1" />
              )}
              <div className={`flex items-center gap-1 sm:gap-1.5 flex-shrink-0 ${isEnding ? 'text-destructive' : 'text-muted-foreground'}`}>
                <Clock size={10} className="sm:w-3 sm:h-3" />
                <span className="text-[10px] sm:text-xs font-medium">{timeRemaining}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-1.5 sm:pt-2 border-t border-border/30">
              <span className="text-[10px] sm:text-xs text-muted-foreground">
                {hasBids ? 'Aktuelles Gebot' : 'Startgebot'}
              </span>
              <div className="flex items-center gap-1 sm:gap-1.5">
                <MoneyIcon size={12} className="sm:w-3.5 sm:h-3.5" />
                <span className="text-xs sm:text-sm font-bold text-foreground">
                  {formatPrice(auction.currentBid)}
                </span>
              </div>
            </div>
            
            {hasBids && (
              <div className="text-[10px] sm:text-xs text-muted-foreground">
                {Object.keys(auction.bids).length} Gebot{Object.keys(auction.bids).length !== 1 ? 'e' : ''}
              </div>
            )}
          </div>
        </div>
      </Link>
    </AuctionCardContextMenu>
  );
});

AuctionCard.displayName = 'AuctionCard';

export default AuctionCard;
